import React, { useRef } from 'react';
import { Cloud, CloudUpload, Download, Upload, Check } from 'lucide-react';
import { bookContent } from '../data/bookContent';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from './ui/dialog';

export const CloudSync = () => {
  const fileInputRef = useRef(null);

  // Gather all journal entries
  const getAllJournalData = () => {
    const data = {
      appName: 'Breakthrough',
      exportDate: new Date().toISOString(),
      version: '1.0',
      entries: {}
    };

    bookContent.chapters.forEach((chapter) => {
      chapter.journalQuestions.forEach((question, qIndex) => {
        const storageKey = `journal-ch${chapter.id}-q${qIndex}`;
        const response = localStorage.getItem(storageKey);
        if (response) {
          data.entries[storageKey] = {
            chapterId: chapter.id,
            chapterTitle: chapter.title,
            questionIndex: qIndex,
            question: question,
            response: response,
            savedAt: new Date().toISOString()
          };
        }
      });
    });

    return data;
  };

  // Count entries
  const getEntryCount = () => {
    let count = 0;
    bookContent.chapters.forEach((chapter) => {
      chapter.journalQuestions.forEach((_, qIndex) => {
        const storageKey = `journal-ch${chapter.id}-q${qIndex}`;
        if (localStorage.getItem(storageKey)) {
          count++;
        }
      });
    });
    return count;
  };

  // Download backup file for iCloud
  const handleBackup = () => {
    const data = getAllJournalData();
    const entryCount = Object.keys(data.entries).length;
    
    if (entryCount === 0) {
      toast.error('No journal entries to backup', {
        description: 'Start writing your reflections first'
      });
      return;
    }

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    // Create filename with date
    const date = new Date().toISOString().split('T')[0];
    link.download = `breakthrough-journal-${date}.json`;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Backup downloaded! (${entryCount} entries)`, {
      description: 'Save this file to iCloud Drive for safekeeping'
    });
  };

  // Restore from backup file
  const handleRestore = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result);
        
        // Validate file structure
        if (!data.appName || data.appName !== 'Breakthrough' || !data.entries) {
          toast.error('Invalid backup file', {
            description: 'Please select a valid Breakthrough journal backup'
          });
          return;
        }

        // Restore entries
        let restoredCount = 0;
        Object.entries(data.entries).forEach(([key, value]) => {
          if (value.response) {
            localStorage.setItem(key, value.response);
            restoredCount++;
          }
        });

        toast.success(`Restored ${restoredCount} journal entries!`, {
          description: 'Your reflections have been recovered'
        });

        // Refresh the page to show restored entries
        setTimeout(() => {
          window.location.reload();
        }, 1500);

      } catch (error) {
        toast.error('Could not read backup file', {
          description: 'The file may be corrupted'
        });
      }
    };

    reader.readAsText(file);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const entryCount = getEntryCount();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid="cloud-sync-btn"
          className="inline-flex items-center gap-2 px-6 py-3 bg-sky text-charcoal rounded-full font-medium hover:bg-sky-dark transition-all shadow-sm"
        >
          <Cloud className="w-5 h-5" />
          iCloud Backup
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Cloud className="w-6 h-6 text-sky" />
            iCloud Backup & Restore
          </DialogTitle>
          <DialogDescription>
            Save your journal to iCloud Drive or restore from a previous backup
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Backup Section */}
          <div className="p-5 bg-gradient-to-r from-sky-light/50 to-sage-light/30 rounded-xl">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                <Download className="w-6 h-6 text-sage" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-charcoal mb-1">Save to iCloud</h3>
                <p className="text-sm text-charcoal-light mb-3">
                  Download your journal and save it to iCloud Drive. You have {entryCount} {entryCount === 1 ? 'entry' : 'entries'} to backup.
                </p>
                <button
                  onClick={handleBackup}
                  data-testid="backup-btn"
                  disabled={entryCount === 0}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  Download Backup
                </button>
              </div>
            </div>
          </div>

          {/* Restore Section */}
          <div className="p-5 bg-cream-paper rounded-xl border border-stone-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                <Upload className="w-6 h-6 text-gold" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-charcoal mb-1">Restore from iCloud</h3>
                <p className="text-sm text-charcoal-light mb-3">
                  Import a previously saved backup file from your iCloud Drive.
                </p>
                <label
                  data-testid="restore-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-sage border border-sage/20 rounded-full font-medium hover:bg-sage/5 transition-all cursor-pointer"
                >
                  <Upload className="w-4 h-4" />
                  Choose Backup File
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleRestore}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="p-4 bg-gold-light/20 rounded-lg border border-gold/20">
            <h4 className="font-medium text-charcoal text-sm mb-2">How to save to iCloud:</h4>
            <ol className="text-xs text-charcoal-light space-y-1">
              <li>1. Click "Download Backup" above</li>
              <li>2. On iPhone/iPad: Choose "Save to Files" → "iCloud Drive"</li>
              <li>3. On Mac: Save to iCloud Drive folder</li>
              <li>4. Your journal is now safely backed up!</li>
            </ol>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
